import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material'
import { type FC, type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { match, P } from 'ts-pattern'
import * as v from 'valibot'
import FluentChevronDown16Filled from '~icons/fluent/chevron-down-16-filled'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import { getBaseCursorPagingArgs } from 'src/components/CursorPaging/cursorPagingUtils'
import {
  MembershipIconCircled,
  TicketIcon,
} from 'src/components/Membership/MembershipIcon'
import { UIButton } from 'src/components/ui/Button'
import {
  UIMenu,
  UIMenuItem,
  UIMenuLabel,
  UIMenuSeparatorSymbol,
  UIMenuTrigger,
} from 'src/components/ui/Menu'
import type { QuotaLogBaseFragment } from 'src/services/apollo/generated/graphql'
import { ExtraPackageBoostType } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionId } from 'src/services/apollo/generation-model'
import { useListUserQuotaLogs } from 'src/services/apollo/quota'
import dayjs from 'src/utils/dayjs'
import { getNodesFromConnection } from 'src/utils/graphql'
import TablePaginationActionsWrapper from '../MuiWrapper/TablePaginationActionsWrapper'
import Text from '../ui/Text'
import { userQuotaLogExtraSchema } from './utils'

/**
 * Copied from packages/pixai-server/src/service/quota.ts
 */
const UserQuotaLogType = {
  daily: 'daily',
  task_cost: 'task_cost',
  referral_reward: 'referral_reward',
  register_gift: 'register_gift',
  app_gift: 'app_gift',
  xmas_gift_2023: 'xmas_gift_2023',
  ad_reward: 'ad_reward',
  membership: 'membership',
  extra_package: 'extra_package',
  task_refund: 'task_refund',
  op: 'op',
  event_cost: 'event_cost',
  event_gift: 'event_gift',
  user_lora_task_cost: 'user_lora_task_cost',
  user_lora_task_refund: 'user_lora_task_refund',
  user_lora_rebate: 'user_lora_rebate',
  user_share: 'user_share',
  quest_reward: 'quest_reward',
  credit_coupon: 'credit_coupon',
  chat_cost: 'chat_cost',
  stamina_purchase: 'stamina_purchase',
  decoration_purchase: 'decoration_purchase',
  decoration_refund: 'decoration_refund',
  contest_reward: 'contest_reward',
  checkin_makeup: 'checkin_makeup',
  checkin_reward: 'checkin_reward',
  kotone_credit_exchange: 'kotone_credit_exchange',
} as const

/**
 * Categorized quota log types
 * Note: Not every type is displayed. This is expected.
 */
const QuotaLogCategories = {
  consumption: {
    label: 'user:quota-log.category.consumption',
    types: [
      UserQuotaLogType.task_cost,
      UserQuotaLogType.chat_cost,
      UserQuotaLogType.decoration_purchase,
      UserQuotaLogType.user_lora_task_cost,
      UserQuotaLogType.event_cost,
      UserQuotaLogType.kotone_credit_exchange,
    ],
  },
  'recharge-membership': {
    label: 'user:quota-log.category.recharge-membership',
    types: [UserQuotaLogType.membership, UserQuotaLogType.extra_package],
  },
  'quest-referral': {
    label: 'user:quota-log.category.quest-referral',
    types: [
      UserQuotaLogType.daily,
      UserQuotaLogType.checkin_reward,
      UserQuotaLogType.quest_reward,
      UserQuotaLogType.ad_reward,
      UserQuotaLogType.user_share,
      UserQuotaLogType.referral_reward,
    ],
  },
  'system-refund': {
    label: 'user:quota-log.category.system-refund',
    types: [
      UserQuotaLogType.task_refund,
      UserQuotaLogType.user_lora_task_refund,
      UserQuotaLogType.user_lora_rebate,
      UserQuotaLogType.op,
    ],
  },
  promotion: {
    label: 'user:quota-log.category.promotion',
    types: [
      UserQuotaLogType.register_gift,
      UserQuotaLogType.app_gift,
      UserQuotaLogType.contest_reward,
      UserQuotaLogType.event_gift,
    ],
  },
} as const

type MenuItem = {
  id: string
  type: 'all' | 'category' | 'item'
  label?: string
  categoryKey?: string
  quotaType?: string
}

function buildMenuItems(): Array<MenuItem | typeof UIMenuSeparatorSymbol> {
  const items: Array<MenuItem | typeof UIMenuSeparatorSymbol> = [
    { id: 'all', type: 'all' },
    UIMenuSeparatorSymbol,
  ]

  const categories = Object.entries(QuotaLogCategories)
  categories.forEach(([categoryKey, category], index) => {
    items.push(
      {
        id: `category-${categoryKey}`,
        type: 'category',
        label: category.label,
        categoryKey,
      },
      ...category.types.map(type => ({
        id: type,
        type: 'item' as const,
        quotaType: type,
      })),
    )

    // Add separator only if not the last category
    if (index < categories.length - 1) {
      items.push(UIMenuSeparatorSymbol)
    }
  })

  return items
}

const menuItems = buildMenuItems()

// Icon mapping for specific quota types
const quotaTypeIcons: Record<string, ReactNode> = {
  [UserQuotaLogType.membership]: <MembershipIconCircled className="size-4" />,
  [UserQuotaLogType.extra_package]: <GameIconsCrystalBars className="size-4" />,
}

function TableRowSkeleton() {
  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell component="th" scope="row">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
    </TableRow>
  )
}

const QuotaLogList: FC<{ userId: string }> = ({ userId }) => {
  const { t } = useTranslation(['user', 'artwork'])

  const [reason, setReason] = useState<string>()

  const [{ before, after }, setCursor] = useState<{
    before?: string
    after?: string
  }>({})

  const { data, loading } = useListUserQuotaLogs({
    reason,
    userId,
    ...getBaseCursorPagingArgs({ before, after, pageSize: 20 }),
  })

  const logs = useMemo(() => getNodesFromConnection(data).reverse(), [data])

  return (
    <div className="flex flex-col gap-4">
      <div>
        <UIMenuTrigger>
          <UIButton
            variant="outlined"
            size="small"
            endIcon={<FluentChevronDown16Filled />}
          >
            {reason ? (
              <Text i18nKey={`user:quota-log-type.${reason}`} />
            ) : (
              <Text i18nKey="common:visibility.all" />
            )}
          </UIButton>
          <UIMenu
            items={menuItems}
            onAction={key => setReason(key === 'all' ? undefined : String(key))}
          >
            {item => {
              if (item.type === 'category') {
                return (
                  <UIMenuLabel>
                    <Text i18nKey={item.label} defaults="Types" />
                  </UIMenuLabel>
                )
              }
              if (item.type === 'all') {
                return (
                  <UIMenuItem id={item.id}>
                    <Text i18nKey="common:visibility.all" />
                  </UIMenuItem>
                )
              }

              const icon = item.quotaType
                ? quotaTypeIcons[item.quotaType]
                : undefined

              return (
                <UIMenuItem id={item.id} icon={icon}>
                  {t(`user:quota-log-type.${item.quotaType}`, {
                    defaultValue: 'Unknown',
                  })}
                </UIMenuItem>
              )
            }}
          </UIMenu>
        </UIMenuTrigger>
      </div>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('user:quota-log.head.credit-change')}</TableCell>
              <TableCell>{t('user:quota-log.head.credit-type')}</TableCell>
              <TableCell>{t('user:quota-log.head.reason')}</TableCell>
              <TableCell align="right">
                {t('user:quota-log.head.date')}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length < 1 && loading && <TableRowSkeleton />}
            {logs.map(log => (
              <TableRow key={log.createdAt}>
                <TableCell component="th" scope="row">
                  <pre>
                    {log.amount > 0 ? '+' : ''}
                    {log.amount.toLocaleString()}
                  </pre>
                </TableCell>
                <TableCell>
                  {t(`user:quota-log-type.${log.type}`, {
                    defaultValue: 'Unknown',
                  })}
                </TableCell>
                <TableCell>
                  <QuotaLogExtra log={log} />
                </TableCell>
                <TableCell align="right">
                  {dayjs(log.createdAt).format('lll')}
                  <span className="text-xs ml-1">
                    ({dayjs(log.createdAt).fromNow()})
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={100}
                align="right"
                sx={{
                  borderBottom: 0,
                }}
              >
                <TablePaginationActionsWrapper
                  pageInfo={data?.pageInfo}
                  onCursorChange={setCursor}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </div>
  )
}

const QuotaLogExtra: FC<{ log: QuotaLogBaseFragment }> = ({ log }) => {
  const parsed = useMemo(() => {
    const result = v.safeParse(userQuotaLogExtraSchema, log.extra)
    return result
  }, [log.extra])

  if (!parsed.success) return null

  const extra = parsed.output
  return match({ ...log, extra })
    .with({ type: UserQuotaLogType.user_lora_rebate }, () => {
      const loraId = extra.loraId
      return loraId ? <QuotaLogExtraLoraInfo loraId={loraId} /> : null
    })
    .with({ type: UserQuotaLogType.extra_package }, () => {
      return <QuotaLogExtraCouponBoost boosts={extra.boosts} />
    })
    .with({ extra: { reason: P.select(P.string) } }, reason => <p>{reason}</p>)
    .otherwise(() => null)
}

const QuotaLogExtraCouponBoost: FC<{
  boosts?: v.InferOutput<typeof userQuotaLogExtraSchema>['boosts']
}> = ({ boosts }) => {
  const couponBoost = boosts?.find(b => b.type === ExtraPackageBoostType.Coupon)
  if (!couponBoost) return null

  return (
    <span className="flex items-center gap-1">
      <TicketIcon />
      <span>+{couponBoost.percentage}%</span>
    </span>
  )
}

const QuotaLogExtraLoraInfo: FC<{ loraId: string }> = ({ loraId }) => {
  const { data } = useGetGenerationModelByVersionId(loraId)

  const displayName = useMemo(() => {
    if (!data) {
      return ''
    }

    const modelTitle = data?.model?.title
    if (modelTitle && modelTitle.trim().length !== 0) {
      return modelTitle
    }

    const versionName = data?.name
    if (versionName && versionName.trim().length !== 0) {
      return versionName
    }

    return data?.id
  }, [data])

  if (!data) return null

  return <Link to={`/model/${data.modelId}`}>{displayName}</Link>
}

export default QuotaLogList
